/**
 * EQUIA · Landing page (Feb 2026 redesign — "Compound / Range fintech-luxury")
 *
 * Design DNA:
 *   • Obsidian (#0A0A0A / #050505) base, gold (#D4AF37) used sparingly as
 *     accent strikes — never as large fills.
 *   • Serif display headlines (Playfair Display), Manrope for body,
 *     JetBrains Mono for numerics — establishes the "private banking + data
 *     terminal" hybrid the brief asked for.
 *   • Sharp edges throughout (no border-radius beyond the gold pill CTA).
 *   • Editorial asymmetry — sections vary in column widths and density.
 *   • Subtle motion only: staggered fades, animated number tickers in hero,
 *     parallax on the hero portfolio mock. No bouncy hovers, no glow.
 *
 * Hard rules (from brief):
 *   • Primary CTA = "Request beta access" → opens existing RequestAccessModal.
 *   • Stack must remain React 18 + Tailwind + framer-motion + lucide-react.
 *     No new deps, no Three.js, no Lottie.
 *   • <1.5s mobile first paint — defer heavy decorative motion; no
 *     >500KB images (we use rendered UI mocks + CSS gradients only).
 *   • Trust signals allowed: "Private beta · invite-only", "GDPR · EU
 *     servers", "ECB FX feeds". Nothing else fabricated.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight,
  ArrowUpRight,
  Bell,
  Building2,
  CalendarRange,
  Check,
  FileText,
  Lock,
  Info,
  Mail,
  Shield,
  ShieldCheck,
  Sparkles,
  Wallet,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import RequestAccessModal from "../components/RequestAccessModal";

// ── Brand assets ────────────────────────────────────────────────────────────
// Hero background — Dubai skyline at night (free Pexels asset, ~180KB, served
// via their CDN with on-the-fly compression). Used as a subtle layer behind
// the portfolio mock — heavily dimmed so the data UI remains the protagonist.
const HERO_BG =
  "https://images.pexels.com/photos/15693274/pexels-photo-15693274.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920";

// ── Mini design tokens ───────────────────────────────────────────────────────
const GOLD = "#D4AF37";

// ── Animated counting number ────────────────────────────────────────────────
function Counter({ value, formatter = (n) => n.toLocaleString(), duration = 1600, startWhenInView = true }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const startedRef = useRef(false);
  useEffect(() => {
    const start = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const t0 = performance.now();
      const tick = (t) => {
        const p = Math.min(1, (t - t0) / duration);
        // ease-out cubic — feels editorial, not bouncy
        const eased = 1 - Math.pow(1 - p, 3);
        setDisplay(value * eased);
        if (p < 1) requestAnimationFrame(tick);
        else setDisplay(value);
      };
      requestAnimationFrame(tick);
    };
    if (!startWhenInView) {
      start();
      return;
    }
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && start()),
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [value, duration, startWhenInView]);
  return <span ref={ref}>{formatter(display)}</span>;
}

// ── Hero portfolio mock ─────────────────────────────────────────────────────
function HeroPortfolioMock() {
  const [ccy, setCcy] = useState("EUR");
  // Mock numbers (this is marketing — no live FX exposure on a public page).
  const totals = useMemo(
    () => ({
      EUR: { sym: "€", value: 4287500, fmt: (n) => `€ ${Math.round(n).toLocaleString()}` },
      AED: { sym: "د.إ", value: 17234760, fmt: (n) => `AED ${Math.round(n).toLocaleString()}` },
      GBP: { sym: "£", value: 3654200, fmt: (n) => `£ ${Math.round(n).toLocaleString()}` },
    }),
    [],
  );
  const t = totals[ccy];

  // Subtle parallax on scroll — only for desktop; gracefully degrades.
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, -40]);

  const assets = [
    {
      flag: "🇦🇪",
      location: "Dubai",
      name: "Marina Bay Tower · 2103",
      type: "Apartment · STR",
      cashflow: "+ AED 18,420 / mo",
      yield: "6.4%",
      yieldColor: "text-emerald-400",
    },
    {
      // North Cyprus (TRNC) has no Unicode emoji — render an inline SVG
      // so the mockup actually looks like the real flag (white field with
      // two red stripes + red crescent and star). Tiny, recognisable.
      flag: (
        <svg viewBox="0 0 30 20" className="w-4 h-3 inline-block" aria-label="North Cyprus flag">
          <rect width="30" height="20" fill="#FFFFFF" />
          <rect y="2.5" width="30" height="1.5" fill="#E30A17" />
          <rect y="16" width="30" height="1.5" fill="#E30A17" />
          {/* Crescent: outer red circle masked by an offset white one */}
          <circle cx="13" cy="10" r="3" fill="#E30A17" />
          <circle cx="14" cy="10" r="2.4" fill="#FFFFFF" />
          {/* Five-pointed star to the right of the crescent */}
          <polygon
            fill="#E30A17"
            points="18.5,8.2 19.1,9.6 20.6,9.7 19.4,10.7 19.9,12.2 18.5,11.3 17.1,12.2 17.6,10.7 16.4,9.7 17.9,9.6"
          />
        </svg>
      ),
      location: "North Cyprus",
      name: "SeaScape 1-Bed",
      type: "Villa · long-term",
      cashflow: "+ £ 1,810 / mo",
      yield: "5.1%",
      yieldColor: "text-emerald-400",
    },
    {
      flag: "🇩🇪",
      location: "Germany",
      name: "Berlin · Mitte 3-bed",
      type: "ETW · long-term",
      cashflow: "− € 124 / mo",
      yield: "3.0%",
      yieldColor: "text-white/85",
    },
  ];

  return (
    <motion.div
      style={{ y }}
      className="relative"
      data-testid="landing-hero-portfolio-mock"
    >
      {/* Ambient gold halo */}
      <div
        aria-hidden
        className="absolute -inset-12 pointer-events-none opacity-40"
        style={{
          background:
            "radial-gradient(60% 50% at 60% 20%, rgba(212,175,55,0.22) 0%, rgba(212,175,55,0) 70%)",
        }}
      />

      <div className="relative bg-[#0A0A0A] border border-white/[0.12] p-5 sm:p-6 max-w-md ml-auto">
        {/* Window chrome */}
        <div className="flex items-center justify-between mb-5 pb-3 border-b border-white/[0.10]">
          <div className="flex items-center gap-2.5">
            <Building2 className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.5} />
            <span className="text-[9px] tracking-[0.2em] uppercase text-white/60 font-sans">
              Portfolio · 3 assets
            </span>
          </div>
          {/* Currency pills */}
          <div className="flex items-center gap-1 bg-[#050505] border border-white/[0.10] p-0.5">
            {["EUR", "AED", "GBP"].map((code) => (
              <button
                key={code}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setCcy(code);
                }}
                data-testid={`hero-ccy-${code.toLowerCase()}`}
                className={`px-2 py-0.5 text-[9px] tracking-[0.15em] uppercase transition-colors font-sans ${
                  ccy === code
                    ? "bg-[#D4AF37] text-black"
                    : "text-white/60 hover:text-white"
                }`}
              >
                {code}
              </button>
            ))}
          </div>
        </div>

        {/* Headline value */}
        <div className="mb-5">
          <p className="text-[9px] tracking-[0.2em] uppercase text-white/55 font-sans mb-1.5">
            Total portfolio value
          </p>
          <p
            className="font-serif text-3xl sm:text-4xl text-white tracking-tight"
            data-testid="hero-total-value"
          >
            <Counter key={ccy} value={t.value} formatter={t.fmt} />
          </p>
          <p className="text-[10px] text-white/55 font-mono mt-1">
            <span className="text-emerald-400">▲ + 6.8%</span> ytd · vs prior 12mo
          </p>
        </div>

        {/* Asset rows */}
        <div className="space-y-1.5">
          {assets.map((a) => (
            <div
              key={a.name}
              className="flex items-center justify-between gap-3 px-3 py-2 bg-[#050505] border border-white/[0.09] hover:border-[#D4AF37]/20 transition-colors"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <span className="text-base leading-none">{a.flag}</span>
                <div className="min-w-0">
                  <p className="text-white text-xs truncate">{a.name}</p>
                  <p className="text-[9px] text-white/55 tracking-wider uppercase font-sans">
                    {a.location} · {a.type}
                  </p>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-[10px] font-mono text-white/90">{a.cashflow}</p>
                <p className={`text-[9px] font-mono ${a.yieldColor}`}>
                  yield {a.yield}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer strip */}
        <div className="mt-4 pt-3 border-t border-white/[0.10] flex items-center justify-between text-[8px] tracking-[0.15em] uppercase text-white/65 font-sans">
          <span>· LIVE ECB FX</span>
          <span className="text-white/60">Updated 09:14 CET</span>
        </div>
      </div>
    </motion.div>
  );
}

// ── Residency card mock ─────────────────────────────────────────────────────
function ResidencyMock() {
  return (
    <div
      className="bg-[#0A0A0A] border border-white/[0.12] p-6 relative overflow-hidden"
      data-testid="landing-residency-mock"
    >
      <div
        aria-hidden
        className="absolute -top-12 -right-12 w-40 h-40 opacity-[0.06]"
        style={{
          background:
            "radial-gradient(closest-side, rgba(212,175,55,0.8), transparent)",
        }}
      />
      <div className="flex items-start justify-between mb-5">
        <div>
          <p className="text-[9px] tracking-[0.2em] uppercase text-white/55 font-sans mb-2">
            Residency · UAE
          </p>
          <h3 className="font-serif text-xl text-white">Golden Visa</h3>
        </div>
        <Shield className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
      </div>

      <div className="space-y-3 mb-5">
        <Row k="Holder" v="A. Müller" />
        <Row k="Expires" v="14 Feb 2027" />
        <Row k="Status" v={<span className="text-emerald-400">Active · 348 days</span>} />
      </div>

      {/* Countdown strip */}
      <div className="relative h-1 bg-white/[0.06] mb-4">
        <div className="absolute inset-y-0 left-0 bg-[#D4AF37]" style={{ width: "68%" }} />
      </div>

      <div className="flex items-start gap-2 px-3 py-2.5 bg-[#D4AF37]/[0.06] border border-[#D4AF37]/20">
        <Bell className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
        <p className="text-[11px] text-white/90 leading-relaxed">
          Renewal reminder scheduled · 60 days before expiry. UAE-specific checklist + estimated fees auto-attached.
        </p>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-white/55 text-[11px] tracking-wider uppercase font-sans">{k}</span>
      <span className="text-white font-mono text-xs">{v}</span>
    </div>
  );
}

// ── Document Vault mock ─────────────────────────────────────────────────────
function VaultMock() {
  const chips = [
    { k: "Buyer", v: "XYZ Holdings GmbH" },
    { k: "Purchase price", v: "€ 690,000" },
    { k: "Title deed", v: "DXB-2103-7754" },
    { k: "Cooling-off", v: "14 days" },
    { k: "Spekulationsfrist", v: "until 03 Feb 2036" },
  ];
  return (
    <div
      className="bg-[#0A0A0A] border border-white/[0.12] p-6"
      data-testid="landing-vault-mock"
    >
      <div className="flex items-center gap-3 mb-5">
        <FileText className="w-4 h-4 text-[#D4AF37]" strokeWidth={1.5} />
        <p className="text-[10px] tracking-[0.2em] uppercase text-white/60 font-sans">
          AI Extraction · 0.8s
        </p>
      </div>

      <div className="bg-[#050505] border border-white/[0.10] px-4 py-3 mb-5 flex items-center gap-3">
        <div className="w-8 h-10 bg-white/[0.04] border border-white/[0.12] flex items-center justify-center flex-shrink-0">
          <FileText className="w-3.5 h-3.5 text-white/60" strokeWidth={1.5} />
        </div>
        <div className="min-w-0">
          <p className="text-white text-xs truncate">SPA_MarinaBay_2103.pdf</p>
          <p className="text-[10px] text-white/65 font-mono">2.4 MB · 14 pages · encrypted</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        {chips.map((c) => (
          <div
            key={c.k}
            className="bg-[#050505] border border-[#D4AF37]/15 px-3 py-2"
          >
            <p className="text-[8px] tracking-[0.2em] uppercase text-white/65 font-sans mb-1">
              {c.k}
            </p>
            <p className="text-white text-[11px] font-mono truncate">{c.v}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Alerts inbox mock ───────────────────────────────────────────────────────
function AlertsMock() {
  const items = [
    {
      tag: "VISA · CRITICAL",
      tagColor: "text-red-400 border-red-500/30 bg-red-500/[0.06]",
      title: "UAE Golden Visa expires in 12 days",
      sub: "Renewal checklist + AED 1,150 est. fee attached",
      icon: Shield,
    },
    {
      tag: "PAYMENT · 14d",
      tagColor: "text-[#D4AF37] border-[#D4AF37]/30 bg-[#D4AF37]/[0.06]",
      title: "Milestone 4 / 8 · Marina Bay",
      sub: "AED 414,000 due to escrow · 03 Mar 2026",
      icon: Wallet,
    },
    {
      tag: "CUSTOM · 30d",
      tagColor: "text-white/90 border-white/15 bg-white/[0.03]",
      title: "Renew building insurance",
      sub: "Email reminder on trigger · scheduled by you",
      icon: Bell,
    },
  ];
  return (
    <div className="space-y-2.5" data-testid="landing-alerts-mock">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <div
            key={it.title}
            className="bg-[#0A0A0A] border border-white/[0.12] p-4 flex items-start gap-4"
          >
            <div className="w-9 h-9 bg-white/[0.03] border border-white/[0.10] flex items-center justify-center flex-shrink-0">
              <Icon className="w-3.5 h-3.5 text-white/85" strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <span
                className={`inline-block text-[9px] tracking-[0.15em] uppercase font-sans px-1.5 py-0.5 border ${it.tagColor} mb-1.5`}
              >
                {it.tag}
              </span>
              <p className="text-white text-sm">{it.title}</p>
              <p className="text-white/60 text-[11px] mt-0.5">{it.sub}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Section primitives ──────────────────────────────────────────────────────
function Eyebrow({ children }) {
  return (
    <p className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37] font-sans mb-4">
      {children}
    </p>
  );
}

function SectionShell({ children, id, testId }) {
  return (
    <section
      id={id}
      data-testid={testId}
      className="relative max-w-[1480px] mx-auto px-5 sm:px-8 py-24 sm:py-32"
    >
      {children}
    </section>
  );
}

function FadeUp({ children, delay = 0, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// ── Trust strip (used twice — under hero + above footer CTA) ────────────────
function TrustStrip({ testId }) {
  const items = [
    { icon: Lock, t: "Private beta · invite-only" },
    {
      icon: ShieldCheck,
      t: "Privacy by design · EU storage",
      // The info tooltip clarifies the processing/storage split honestly:
      // stored data lives in EU data centres, but the application is
      // currently hosted by Emergent in non-EU infrastructure under the
      // user's explicit Art. 49(1)(a) GDPR consent. Spec styling: bg #111,
      // border 0.5px solid #2a2a2a, color #C8C0A8, 12px, 6px radius,
      // 8px 12px pad, max-width 280px. Tooltip links to the Privacy
      // Policy sub-processor section so legal-conscious visitors can
      // drill down.
      tooltip: (
        <>
          Account data, portfolios and encrypted documents are stored in EU data centres (Frankfurt).
          AI processing currently routes through non-EU infrastructure under your explicit consent —
          see our{" "}
          <a
            href="/privacy#sub-processors"
            className="underline hover:text-[#D4AF37] transition-colors"
          >
            Privacy Policy
          </a>{" "}
          for the full sub-processor list and our EU-region roadmap.
        </>
      ),
    },
    { icon: Sparkles, t: "Live ECB FX feeds" },
  ];
  return (
    <div
      data-testid={testId}
      className="flex items-center justify-center flex-wrap gap-x-8 gap-y-3 py-5 border-y border-white/[0.09]"
    >
      {items.map(({ icon: Icon, t, tooltip }) => (
        <div key={t} className="flex items-center gap-2 relative group">
          <Icon className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.5} />
          <span className="text-[10px] tracking-[0.2em] uppercase text-white/65 font-sans">{t}</span>
          {tooltip && (
            <>
              <button
                type="button"
                aria-label="Data-residency details"
                data-testid="gdpr-tooltip-trigger"
                className="text-white/45 hover:text-[#D4AF37] focus:text-[#D4AF37] focus:outline-none transition-colors"
                // No onClick handler — the tooltip is purely hover/focus driven.
                // tabIndex inherited from <button> makes it keyboard-accessible
                // (focus-visible reveals the same tooltip).
              >
                <Info className="w-3 h-3" strokeWidth={1.8} />
              </button>
              <div
                role="tooltip"
                data-testid="gdpr-tooltip-content"
                className="
                  absolute left-1/2 -translate-x-1/2 top-full mt-2 z-30
                  opacity-0 pointer-events-none
                  group-hover:opacity-100 group-hover:pointer-events-auto
                  group-focus-within:opacity-100 group-focus-within:pointer-events-auto
                  transition-opacity duration-150
                  bg-[#111] text-[#C8C0A8] text-[12px] leading-relaxed
                  px-3 py-2 max-w-[280px] w-max
                "
                style={{
                  border: "0.5px solid #2a2a2a",
                  borderRadius: "6px",
                  padding: "8px 12px",
                }}
              >
                {tooltip}
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// LANDING PAGE
// ────────────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const { user, loading } = useAuth();
  const [requestOpen, setRequestOpen] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-6 h-6 border border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (user) return <Navigate to="/portfolio" replace />;

  const openRequest = () => setRequestOpen(true);

  return (
    <div className="min-h-screen bg-[#050505] text-white antialiased" data-testid="landing-page">
      {/* Ambient grain overlay (CSS noise via SVG data-uri — no asset weight) */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0 opacity-[0.035] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.55'/></svg>\")",
        }}
      />

      {/* ── TOP UTILITY BAR ─────────────────────────────────────────────── */}
      <header className="relative z-30 max-w-[1480px] mx-auto px-5 sm:px-8 pt-6 sm:pt-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3" data-testid="landing-wordmark">
          <img
            src="/equia-logo.png"
            alt="EQUIA Global Property"
            className="h-9 sm:h-10 w-auto"
          />
        </Link>
        <div className="flex items-center gap-5">
          <Link
            to="/login"
            data-testid="landing-signin"
            className="text-xs tracking-[0.15em] uppercase text-white/80 hover:text-white transition-colors font-sans"
          >
            Sign in
          </Link>
          <button
            type="button"
            onClick={openRequest}
            data-testid="landing-cta-nav"
            className="hidden sm:inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#B5952F] text-black text-[11px] tracking-[0.15em] uppercase font-medium px-4 py-2 transition-colors"
          >
            Request beta access
            <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </header>

      {/* ── HERO · Full-bleed Dubai skyline · copy left-anchored ─────────── */}
      <section
        data-testid="landing-section-hero"
        className="relative z-10 -mt-[88px] sm:-mt-[104px] min-h-[92vh] flex items-center overflow-hidden"
      >
        {/* Full-bleed skyline. opacity raised to 0.85 so the city is the
            visual protagonist; we keep just enough darkening on the LEFT
            third for headline legibility. */}
        <div aria-hidden className="absolute inset-0 -z-10">
          <img
            src={HERO_BG}
            alt=""
            loading="eager"
            className="absolute inset-0 w-full h-full object-cover opacity-70"
          />
          {/* Global dark scrim so the skyline reads as a subtle backdrop, not
              the protagonist. */}
          <div className="absolute inset-0 bg-black/45" />
          {/* Left → right gradient: opaque charcoal on the left for text
              contrast, lightly veiled on the right so the skyline still breathes. */}
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(90deg, #050505 0%, rgba(5,5,5,0.85) 30%, rgba(5,5,5,0.55) 55%, rgba(5,5,5,0.30) 100%)",
            }}
          />
          {/* Bottom-up fade so the trust strip sits cleanly on dark. */}
          <div
            className="absolute inset-x-0 bottom-0 h-40"
            style={{
              background:
                "linear-gradient(180deg, rgba(5,5,5,0) 0%, #050505 100%)",
            }}
          />
        </div>

        <div className="relative max-w-[1480px] w-full mx-auto px-5 sm:px-8 pt-[112px] sm:pt-[140px] pb-28 sm:pb-32">
          <FadeUp>
            {/* eyebrow pill */}
            <div className="inline-flex items-center gap-2 mb-8 px-2.5 py-1 border border-white/15 bg-black/40 backdrop-blur-sm">
              <span className="w-1.5 h-1.5 bg-[#D4AF37] inline-block" />
              <span className="text-[9px] tracking-[0.25em] uppercase text-white/90 font-sans">
                Private beta · by invitation
              </span>
            </div>

            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-white leading-[1.02] tracking-tight max-w-3xl">
              Your global property empire,
              <br />
              <span className="text-[#D4AF37] italic font-normal">one dashboard.</span>
            </h1>

            <p className="mt-8 text-white/90 text-base sm:text-lg leading-relaxed max-w-xl">
              EQUIA is the operating system for international real-estate investors with portfolios across Dubai, North Cyprus and Germany. Multi-currency intelligence, AI document analysis and regulatory awareness — all in one interface designed for the long game.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-5">
              <button
                type="button"
                onClick={openRequest}
                data-testid="landing-cta-hero"
                className="group inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#B5952F] text-black text-xs sm:text-[13px] tracking-[0.18em] uppercase font-medium px-8 py-4 transition-colors"
              >
                Request beta access
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
              </button>
              <Link
                to="/login"
                data-testid="landing-cta-signin"
                className="inline-flex items-center gap-2 text-xs sm:text-[13px] tracking-[0.18em] uppercase text-white/90 hover:text-white transition-colors font-sans"
              >
                <Mail className="w-3.5 h-3.5" strokeWidth={1.5} />
                Already invited? Sign in
              </Link>
            </div>
          </FadeUp>

          {/* Bottom trust strip — sits at the foot of the hero, no border so
              it floats over the gradient transition. */}
          <div className="absolute bottom-10 left-5 sm:left-8 right-5 sm:right-8 flex items-center flex-wrap gap-x-8 gap-y-3">
            {[
              { icon: Lock, t: "Privacy by design · EU storage" },
              { icon: ShieldCheck, t: "Encrypted end-to-end" },
              { icon: Sparkles, t: "Live ECB FX feeds" },
            ].map(({ icon: Icon, t }) => (
              <div key={t} className="flex items-center gap-2">
                <Icon className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.5} />
                <span className="text-[10px] tracking-[0.2em] uppercase text-white/80 font-sans">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STOP-JUGGLING SECTION ───────────────────────────────────────── */}
      <SectionShell testId="landing-section-stop-juggling">
        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-12 lg:gap-20 items-start">
          <FadeUp>
            <Eyebrow>The brief</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.08] tracking-tight">
              Stop juggling spreadsheets,
              <br />
              folders, and visa deadlines.
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <p className="text-white/80 text-base leading-relaxed">
              The cross-border investor pays the same tax twice — once in cash, once in cognitive load. EQUIA is the quiet system underneath: it remembers when your Golden Visa lapses, parses the contract you signed in November, and reconciles your AED rental income into the EUR balance you actually live in.
            </p>
            <div className="mt-7 grid grid-cols-3 gap-4">
              {[
                { v: "11", l: "Spreadsheets retired" },
                { v: "3", l: "Jurisdictions, one view" },
                { v: "0", l: "Missed renewals" },
              ].map((s) => (
                <div key={s.l} className="border-l border-[#D4AF37]/40 pl-4">
                  <p className="font-serif text-3xl text-white">{s.v}</p>
                  <p className="text-[10px] tracking-[0.15em] uppercase text-white/60 font-sans mt-1">{s.l}</p>
                </div>
              ))}
            </div>
          </FadeUp>
        </div>
      </SectionShell>

      {/* ── PORTFOLIO · MULTI-CURRENCY ──────────────────────────────────── */}
      <div className="border-t border-white/[0.09]">
        <SectionShell testId="landing-section-portfolio">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-20 items-center">
            <FadeUp>
              <Eyebrow>01 · Portfolio</Eyebrow>
              <h2 className="font-serif text-3xl sm:text-4xl text-white leading-tight tracking-tight">
                Three jurisdictions.
                <br />
                One running total.
              </h2>
              <p className="mt-6 text-white/80 text-base leading-relaxed">
                A single Multi-Currency Dashboard pulls cashflow, equity yield, net yield and cap rate from every asset you own — Dubai apartment, Cyprus villa, Berlin Eigentumswohnung — and lets you read them all in the currency you actually think in. Rates feed live from the European Central Bank.
              </p>
              <ul className="mt-7 space-y-3 text-sm text-white/85">
                {[
                  "Cashflow & equity yield per asset · in your home currency",
                  "Toggle EUR / AED / GBP — instantly, with live ECB rates",
                  "Cap Rate, Bruttorendite, Nettorendite — matched to your Excel to the cent",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <Check className="w-3.5 h-3.5 text-[#D4AF37] mt-1 flex-shrink-0" strokeWidth={2} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </FadeUp>
            <FadeUp delay={0.15}>
              <HeroPortfolioMock />
            </FadeUp>
          </div>
        </SectionShell>
      </div>

      {/* ── RESIDENCY & VISA TRACKER (the explicit user-add emphasis) ──── */}
      <div className="border-t border-white/[0.09] bg-[#0A0A0A]">
        <SectionShell testId="landing-section-residency">
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-20 items-center">
            <FadeUp delay={0.1}>
              <ResidencyMock />
              <div className="mt-5 grid grid-cols-3 gap-3">
                {[
                  { d: "14 Feb", l: "UAE Golden Visa" },
                  { d: "22 May", l: "NC Residency" },
                  { d: "07 Sep", l: "Schengen 90/180" },
                ].map((p, i) => (
                  <div
                    key={p.l}
                    className={`bg-[#050505] border ${i === 0 ? "border-[#D4AF37]/30" : "border-white/[0.10]"} px-3 py-3`}
                  >
                    <p className={`font-mono text-xs ${i === 0 ? "text-[#D4AF37]" : "text-white/90"}`}>
                      {p.d}
                    </p>
                    <p className="text-[9px] tracking-[0.15em] uppercase text-white/55 font-sans mt-1">{p.l}</p>
                  </div>
                ))}
              </div>
            </FadeUp>

            <FadeUp>
              <Eyebrow>02 · Compliance</Eyebrow>
              <h2 className="font-serif text-3xl sm:text-4xl text-white leading-tight tracking-tight">
                Never miss a renewal again.
              </h2>
              <p className="mt-6 text-white/80 text-base leading-relaxed">
                The Residency Tracker watches every permit you hold — UAE Golden Visa, North Cyprus residency, Schengen 90/180 — and surfaces an alert sixty days before expiry, with a country-specific renewal checklist and estimated fees already attached.
              </p>
              <ul className="mt-7 space-y-3 text-sm text-white/85">
                {[
                  "Automated expiration reminders · email + in-app",
                  "Country-specific checklists with estimated fees",
                  "Schengen 90/180 day-counter for German residents",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <Check className="w-3.5 h-3.5 text-[#D4AF37] mt-1 flex-shrink-0" strokeWidth={2} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </FadeUp>
          </div>
        </SectionShell>
      </div>

      {/* ── AI DOCUMENT VAULT ───────────────────────────────────────────── */}
      <div className="border-t border-white/[0.09]">
        <SectionShell testId="landing-section-vault">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-20 items-center">
            <FadeUp>
              <Eyebrow>03 · Documents</Eyebrow>
              <h2 className="font-serif text-3xl sm:text-4xl text-white leading-tight tracking-tight">
                The contract reads itself.
              </h2>
              <p className="mt-6 text-white/80 text-base leading-relaxed">
                Drop a purchase contract, title deed, or residency card into the encrypted vault. Within a second, the AI extracts the buyer, the price, the cooling-off period, the Spekulationsfrist window — and files it alongside the right property. Everything stays encrypted at rest on EU-hosted storage.
              </p>
              <ul className="mt-7 space-y-3 text-sm text-white/85">
                {[
                  "Drag-and-drop SPAs, title deeds, residency cards, utility bills",
                  "AI parses parties, dates, amounts and compliance clauses",
                  "End-to-end encrypted · GDPR-compliant · EU storage",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3">
                    <Check className="w-3.5 h-3.5 text-[#D4AF37] mt-1 flex-shrink-0" strokeWidth={2} />
                    <span>{t}</span>
                  </li>
                ))}
              </ul>
            </FadeUp>
            <FadeUp delay={0.15}>
              <VaultMock />
            </FadeUp>
          </div>
        </SectionShell>
      </div>

      {/* ── SMART ALERTS ────────────────────────────────────────────────── */}
      <div className="border-t border-white/[0.09] bg-[#0A0A0A]">
        <SectionShell testId="landing-section-alerts">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-20 items-center">
            <FadeUp>
              <Eyebrow>04 · Alerts</Eyebrow>
              <h2 className="font-serif text-3xl sm:text-4xl text-white leading-tight tracking-tight">
                One inbox for every deadline that matters.
              </h2>
              <p className="mt-6 text-white/80 text-base leading-relaxed">
                Visa expiries, off-plan payment milestones, your own custom reminders — all surfaced in one quiet inbox. Choose to receive a Resend-delivered email on the trigger day, or just see it appear here when it's close.
              </p>
              <div className="mt-7 flex items-center gap-3 text-xs text-white/60">
                <CalendarRange className="w-3.5 h-3.5 text-[#D4AF37]" strokeWidth={1.5} />
                <span>Color-coded by urgency · overdue / 3d / 14d / 30d</span>
              </div>
            </FadeUp>
            <FadeUp delay={0.15}>
              <AlertsMock />
            </FadeUp>
          </div>
        </SectionShell>
      </div>

      {/* ── HOW IT WORKS · The onboarding ritual ────────────────────────── */}
      <div className="border-t border-white/[0.09]">
        <SectionShell testId="landing-section-onboarding">
          <FadeUp>
            <div className="max-w-2xl mb-14">
              <Eyebrow>How it works</Eyebrow>
              <h2 className="font-serif text-3xl sm:text-4xl text-white leading-tight tracking-tight">
                Three steps. Then EQUIA runs itself.
              </h2>
              <p className="mt-5 text-white/80 text-base leading-relaxed">
                We onboard a small number of investors each month — personally, by hand. The first session, we guide you through everything. After that, it runs automatically.
              </p>
            </div>
          </FadeUp>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-white/[0.06]">
            {[
              {
                n: "01",
                title: "Request access",
                body: "Submit a short application. We review every request personally — typically within 48 hours.",
              },
              {
                n: "02",
                title: "Personal setup",
                body: "Once approved, we walk through your portfolio together. Properties, residencies and key documents are imported in a single private session.",
              },
              {
                n: "03",
                title: "Runs in the background",
                body: "EQUIA watches deadlines, parses documents and reconciles currencies automatically. You only hear from it when it matters.",
              },
            ].map((step, i) => (
              <FadeUp key={step.n} delay={i * 0.1}>
                <div className="bg-[#050505] p-8 sm:p-10 h-full flex flex-col">
                  <div className="flex items-baseline justify-between mb-6">
                    <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">
                      Step {step.n}
                    </span>
                    <span className="font-serif text-3xl text-white/10">{step.n}</span>
                  </div>
                  <h3 className="font-serif text-xl sm:text-2xl text-white tracking-tight mb-3">
                    {step.title}
                  </h3>
                  <p className="text-white/65 text-sm leading-relaxed">{step.body}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </SectionShell>
      </div>

      {/* ── FINAL CTA ───────────────────────────────────────────────────── */}
      <div
        className="relative border-t border-white/[0.09] overflow-hidden"
        data-testid="landing-section-final-cta"
      >
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none opacity-50"
          style={{
            background:
              "radial-gradient(60% 40% at 50% 100%, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0) 70%)",
          }}
        />
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 py-24 sm:py-32 text-center">
          <FadeUp>
            <Eyebrow>Built for portfolios that don't fit on a spreadsheet</Eyebrow>
            <h2 className="font-serif text-3xl sm:text-5xl lg:text-6xl text-white leading-[1.08] tracking-tight">
              Quiet command for the
              <br />
              <span className="text-[#D4AF37] italic font-normal">cross-border investor.</span>
            </h2>
            <button
              type="button"
              onClick={openRequest}
              data-testid="landing-cta-final"
              className="group mt-10 inline-flex items-center gap-3 bg-[#D4AF37] hover:bg-[#B5952F] text-black text-xs sm:text-[13px] tracking-[0.18em] uppercase font-medium px-8 py-4 transition-colors"
            >
              Request beta access
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
            <div className="mt-10">
              <TrustStrip testId="landing-trust-strip-bottom" />
            </div>
          </FadeUp>
        </div>
      </div>

      {/* ── FOOTER ──────────────────────────────────────────────────────── */}
      <footer className="border-t border-white/[0.09] bg-[#050505]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8">
            <div className="col-span-2 sm:col-span-1">
              <img
                src="/equia-logo.png"
                alt="EQUIA"
                className="h-8 w-auto mb-3 opacity-80"
              />
              <p className="text-[11px] text-white/55 leading-relaxed max-w-xs">
                Made for the cross-border investor. Quiet command for a global property life.
              </p>
            </div>
            {[
              {
                t: "Legal",
                links: [
                  ["Legal Notice", "/legal"],
                  ["Privacy Policy", "/privacy"],
                ],
              },
            ].map((col) => (
              <div key={col.t}>
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/55 font-sans mb-3">
                  {col.t}
                </p>
                <ul className="space-y-2">
                  {col.links.map(([label, href, onClick]) => (
                    <li key={label}>
                      {onClick ? (
                        <button
                          type="button"
                          onClick={onClick}
                          className="text-xs text-white/85 hover:text-white transition-colors"
                        >
                          {label}
                        </button>
                      ) : (
                        <Link
                          to={href}
                          className="text-xs text-white/85 hover:text-white transition-colors"
                        >
                          {label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-10 pt-6 border-t border-white/[0.09] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-[10px] tracking-wider text-white/65">
              © {new Date().getFullYear()} EQUIA Global Property. All rights reserved.
            </p>
            <div className="flex items-center gap-2 text-[10px] tracking-[0.15em] uppercase text-white/65">
              <Mail className="w-3 h-3" strokeWidth={1.5} />
              <a href="mailto:invitations@getequia.com" className="hover:text-white/90 transition-colors">
                invitations@getequia.com
              </a>
            </div>
          </div>
        </div>
      </footer>

      <RequestAccessModal open={requestOpen} onClose={() => setRequestOpen(false)} />
    </div>
  );
}
